import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ReviewDecision, type BookingReviewItem } from "@/app/booking-calendar/api/reviewApi"
import { useGetBookingQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { AppointmentDialog } from "@/app/booking-calendar/components/modals"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { useGetProcessedReviewItemsQuery } from "./processedReviewApi"
import { PROCESSED_PAGE_SIZE, decidedAtLabel, decisionLabel, processedPage } from "./processedReview"
import { appointmentWindowLabel } from "./reviewBookingSummary"

/**
 * The review queue's processed items (AB#841): what was decided, by whom and when, newest decision
 * first, a page at a time, with the confirmation number and the reviewer's comments (AB#797) so the
 * archive shows what was done and why. Read-only: a row opens the appointment itself, the same dialog the calendar
 * and Search use, and a request that never reached the scheduling system has nothing to open. The Action
 * column shows the decision taken, with View (or "Not booked") under it (AB#797).
 */
export function ProcessedReviewList() {
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<BookingReviewItem | null>(null)

  const query = useGetProcessedReviewItemsQuery(
    { skip: page * PROCESSED_PAGE_SIZE, take: PROCESSED_PAGE_SIZE },
    { refetchOnMountOrArgChange: true },
  )
  const selectedBooking = useGetBookingQuery(selected?.calBookingUid ?? "", { skip: !selected?.calBookingUid })

  const { items, hasMore } = processedPage(query.currentData ?? [], PROCESSED_PAGE_SIZE)

  return (
    <div className="flex flex-col gap-2" data-testid="processed-review-list">
      {query.isError ? (
        <div className="rounded-xl border bg-card p-6 text-destructive">
          The processed items could not be loaded.
        </div>
      ) : !query.currentData ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          {page === 0 ? "Nothing has been processed yet." : "No more processed items."}
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reviewer</TableHead>
                <TableHead>Decided (Pacific)</TableHead>
                <TableHead>Appointment</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Comments</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid="processed-review-row">
                  <TableCell>{item.decisionMadeBy ?? "—"}</TableCell>
                  <TableCell>{decidedAtLabel(item.decisionMadeAt) ?? "—"}</TableCell>
                  <TableCell>
                    <div>{appointmentWindowLabel(item.slotStart, item.slotEnd) ?? "—"}</div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-mono" data-testid="processed-review-code">{item.confirmationCode || "—"}</span> · {item.siteName ?? "—"}
                    </div>
                  </TableCell>
                  <TableCell>{item.attendeeName || "—"}</TableCell>
                  <TableCell className="max-w-xs whitespace-pre-wrap break-words" data-testid="processed-review-comments">
                    {item.userComments?.trim() || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        variant={item.reviewDecision === ReviewDecision.CancelDuplicate ? "destructive" : "secondary"}
                        data-testid="processed-review-action"
                      >
                        {decisionLabel(item.reviewDecision)}
                      </Badge>
                      {item.calBookingUid ? (
                        <Button variant="outline" size="sm" onClick={() => setSelected(item)}>
                          View
                        </Button>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not booked</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-muted-foreground">Page {page + 1}</span>
        <Button variant="outline" size="sm" disabled={page === 0 || query.isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={!hasMore || query.isFetching} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
      </div>

      {selected && selectedBooking.isError && (
        <div className="flex items-center justify-between gap-2 rounded-xl border bg-card p-4 text-sm">
          <span className="text-destructive">That appointment could not be loaded from the scheduling system.</span>
          <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Dismiss</Button>
        </div>
      )}

      {selected && (
        <AppointmentDialog
          booking={selectedBooking.data}
          staffName={selected.hostName ?? undefined}
          timeZone={SITE_TIME_ZONE}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
